import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Typography from "../../../Utility/Typography";
import Button from "../../../Utility/Button/Button";
import FormSubmitButton from "../../../Utility/Button/FormSubmitButton";
import Dropdown from "../../../Utility/Dropdown/Dropdown";
import ErrorMsg from "../../../Utility/ErrorMsg";
import CommonModal from "../../../Utility/CommonModel/CommonModal";
import { addTeamMember } from "../../../Redux/Thunks/teamsThunk";
import { fetchUsersData } from "../../../Redux/Thunks/usersThunk";
import {
  selectUsersAllListData,
  selectUsersData,
  selectUsersLoading,
} from "../../../Redux/Slices/usersSlice";
import {
  mergeUsersListFromApiPayloads,
  resolveUserDisplayName,
  resolveUserEmailFromRecord,
} from "../../Users/usersDataUtils";
import { getUserAssigneeInitials } from "../../Issues/issueMappers";
import { getUserAvatarImageUrl } from "../../../utils/userFilterOptions";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";

const addTeamMemberPickerSchema = yup.object().shape({
  email: yup.string().trim().required("User is required"),
});

const AddTeamMemberModal = ({
  isOpen,
  onClose,
  team,
  onSuccess,
  onError,
  /** Emails already on this team — hidden from the picker */
  excludeMemberEmails = [],
}) => {
  const dispatch = useDispatch();
  const usersAllListData = useSelector(selectUsersAllListData);
  const usersPagedData = useSelector(selectUsersData);
  const usersLoading = useSelector(selectUsersLoading);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchUsersData({ getAll: true, force: true }));
  }, [dispatch, isOpen]);

  const usersList = useMemo(
    () => mergeUsersListFromApiPayloads(usersAllListData, usersPagedData),
    [usersAllListData, usersPagedData]
  );

  const userOptions = useMemo(() => {
    const exclude = new Set(
      (excludeMemberEmails ?? []).map((e) => String(e).trim().toLowerCase()).filter(Boolean)
    );

    return usersList
      .filter((u) => {
        const email = resolveUserEmailFromRecord(u).toLowerCase();
        if (email && exclude.has(email)) return false;
        return true;
      })
      .map((u) => {
        const email = resolveUserEmailFromRecord(u);
        const name = resolveUserDisplayName(u);
        const id = u.id ?? u.userId;
        const avatarUrl = getUserAvatarImageUrl(u);
        return {
          value: email || (id != null ? String(id) : name),
          label: name,
          showAvatar: true,
          avatar: getUserAssigneeInitials(u),
          ...(avatarUrl ? { avatarUrl } : {}),
          searchText: [name, email, id != null ? String(id) : ""].filter(Boolean).join(" "),
        };
      });
  }, [usersList, excludeMemberEmails]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(addTeamMemberPickerSchema),
    defaultValues: {
      email: "",
    },
  });

  const resolveSubmitEmail = (selectedValue) => {
    const value = String(selectedValue ?? "").trim();
    if (!value) return "";
    if (value.includes("@")) return value;
    const user = usersList.find((u) => String(u.id ?? u.userId) === value);
    return resolveUserEmailFromRecord(user);
  };

  const onSubmit = async (data) => {
    if (!team?.id) {
      setErrorMsg("Missing team.");
      return;
    }
    const email = resolveSubmitEmail(data.email);
    if (!email || !email.includes("@")) {
      setErrorMsg("Please select a user with a valid email.");
      return;
    }
    setErrorMsg("");
    setLoading(true);
    try {
      await dispatch(
        addTeamMember({
          teamId: team.id,
          email,
        })
      ).unwrap();
      reset();
      if (onSuccess) onSuccess({ ...data, email, teamId: team.id });
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      const msg = getApiErrorMessage(err, "Failed to add team member.");
      setErrorMsg(msg || "Failed to add team member.");
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      setErrorMsg("");
      onClose();
    }
  };

  if (!isOpen) return null;

  const userPlaceholder = usersLoading
    ? "Loading users..."
    : userOptions.length === 0
      ? "No users available"
      : "Select user";

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Team Member"
      width="max-w-md"
      loading={loading}
    >
      <Typography variant="text2" className="text-text-secondary mb-4 block">
        Select a user to add to this team
      </Typography>
      {errorMsg && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <Typography className="text-red-700 text-sm">{errorMsg}</Typography>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <Dropdown
                {...field}
                name="email"
                label="User"
                value={value}
                onChange={(event) => onChange(event?.target?.value ?? event)}
                options={userOptions}
                placeholder={userPlaceholder}
                error={errors.email?.message}
                disabled={loading || usersLoading}
                required={true}
                searchable={true}
                className="text-sm"
              />
            )}
          />
          <ErrorMsg message={errors.email?.message} className="mt-1" />
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button
            type="button"
            variant="outline"
            size="md"
            disabled={loading}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <FormSubmitButton
            loading={loading}
            label="Send Invitation"
            loadingText="Sending..."
            idleIconClass=""
            size="md"
            disabled={loading || usersLoading || userOptions.length === 0}
          />
        </div>
      </form>
    </CommonModal>
  );
};

export default AddTeamMemberModal;
